import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowLeft, Loader2, BookOpen } from 'lucide-react';
import { selfAssessmentApi, QuestionWithUserAnswer, SelfAssessmentAttempt } from '../api/selfAssessment';

export const SelfAssessmentReview: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [attempt, setAttempt] = useState<SelfAssessmentAttempt | null>(null);
  const [questions, setQuestions] = useState<QuestionWithUserAnswer[]>([]);

  useEffect(() => {
    const loadAttemptDetails = async () => {
      try {
        setLoading(true);
        const data = await selfAssessmentApi.getAttemptWithQuestions(attemptId!);
        setAttempt(data.attempt);
        setQuestions(data.questions);
      } catch (err: any) {
        setError('Kon resultaten niet laden. Probeer het later opnieuw.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (attemptId) {
      loadAttemptDetails();
    }
  }, [attemptId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Resultaten laden...</p>
        </div>
      </div>
    );
  }

  if (error || !attempt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Fout bij laden
            </h2>
            <p className="text-gray-600 mb-6">
              {error || 'Kon resultaten niet laden'}
            </p>
            <button
              onClick={() => navigate('/self-assessment')}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
            >
              Terug naar Zelftest
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Group questions by label
  const questionsByLabel: Record<string, QuestionWithUserAnswer[]> = {};
  questions.forEach(q => {
    if (!questionsByLabel[q.label]) {
      questionsByLabel[q.label] = [];
    }
    questionsByLabel[q.label].push(q);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/self-assessment')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar Zelftest
          </button>
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gedetailleerde Resultaten
            </h1>
            <p className="text-gray-600">
              Bekijk je antwoorden en leer van de uitleg bij elke vraag
            </p>
            <div className="mt-4 flex items-center space-x-6">
              <div>
                <span className="text-sm text-gray-600">Totale Score:</span>
                <span className="ml-2 text-2xl font-bold text-blue-600">
                  {attempt.score} / {questions.length}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Percentage:</span>
                <span className="ml-2 text-2xl font-bold text-blue-600">
                  {Math.round((attempt.score! / questions.length) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Questions by Category */}
        {Object.entries(questionsByLabel).map(([label, labelQuestions]) => {
          const correctInLabel = labelQuestions.filter(q => q.isCorrect).length;
          const totalInLabel = labelQuestions.length;
          const percentageInLabel = Math.round((correctInLabel / totalInLabel) * 100);

          return (
            <div key={label} className="mb-6">
              <div className="bg-white rounded-xl shadow-md p-4 mb-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900">{label}</h2>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">
                      {correctInLabel} / {totalInLabel} correct
                    </div>
                    <div className={`text-base font-bold ${
                      percentageInLabel >= 80 ? 'text-green-600' :
                      percentageInLabel >= 60 ? 'text-blue-600' :
                      percentageInLabel >= 40 ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      {percentageInLabel}%
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {labelQuestions.map((question, index) => {
                  const choices = [
                    { key: 'A', text: question.choice_a },
                    { key: 'B', text: question.choice_b },
                    { key: 'C', text: question.choice_c },
                    { key: 'D', text: question.choice_d }
                  ];

                  return (
                    <div key={question.id} className="bg-white rounded-xl shadow-md p-4">
                      {/* Question Header */}
                      <div className="flex items-start mb-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mr-3 ${
                          question.isCorrect
                            ? 'bg-green-100'
                            : 'bg-red-100'
                        }`}>
                          {question.isCorrect ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 mb-1">
                            Vraag {question.order_index}
                          </div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {question.question}
                          </h3>
                        </div>
                      </div>

                      {/* Answer Choices */}
                      <div className="space-y-2 mb-3">
                        {choices.map((choice) => {
                          const isUserAnswer = question.userAnswer === choice.key;
                          const isCorrectAnswer = question.correct_answer === choice.key;
                          
                          let bgColor = 'bg-gray-50';
                          let borderColor = 'border-gray-200';
                          let textColor = 'text-gray-700';

                          if (isCorrectAnswer) {
                            bgColor = 'bg-green-50';
                            borderColor = 'border-green-500';
                            textColor = 'text-green-900';
                          } else if (isUserAnswer && !isCorrectAnswer) {
                            bgColor = 'bg-red-50';
                            borderColor = 'border-red-500';
                            textColor = 'text-red-900';
                          }

                          return (
                            <div
                              key={choice.key}
                              className={`p-3 rounded-lg border-2 ${bgColor} ${borderColor}`}
                            >
                              <div className="flex items-start">
                                <span className={`font-semibold mr-3 ${textColor} text-sm`}>
                                  {choice.key}.
                                </span>
                                <span className={`${textColor} text-sm`}>
                                  {choice.text}
                                </span>
                                {isCorrectAnswer && (
                                  <CheckCircle className="w-4 h-4 text-green-600 ml-auto flex-shrink-0" />
                                )}
                                {isUserAnswer && !isCorrectAnswer && (
                                  <XCircle className="w-4 h-4 text-red-600 ml-auto flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Explanation */}
                      {question.explanation && (
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <div className="flex items-start">
                            <BookOpen className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold text-blue-900 mb-1 text-sm">
                                Uitleg
                              </h4>
                              <p className="text-blue-800 text-xs leading-relaxed">
                                {question.explanation}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Bottom Actions */}
        <div className="bg-white rounded-2xl shadow-xl p-6 flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/self-assessment')}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
          >
            Opnieuw Doen
          </button>
          <button
            onClick={() => navigate('/topics')}
            className="flex-1 px-6 py-3 bg-white text-gray-700 border-2 border-gray-200 rounded-xl font-semibold hover:border-blue-300 hover:bg-blue-50 transition-all"
          >
            Bekijk Trainingen
          </button>
        </div>
      </div>
    </div>
  );
};

